/**
 * Calendar Day View
 * Display appointments in a detailed hourly timeline for a single day
 */

import type { AppointmentWithDetails } from '../../hooks/useAppointments';
import {
  formatDate,
  formatTime,
  getStatusColor,
  timeToMinutes,
  getTimeFromISO,
  isSameDay,
  parseISODateTime,
  formatDuration,
} from '../../utils/calendar';

interface CalendarDayViewProps {
  date: Date;
  appointments: AppointmentWithDetails[];
  onAppointmentClick: (appointment: AppointmentWithDetails) => void;
  onTimeSlotClick: (time: string) => void;
}

export function CalendarDayView({
  date,
  appointments,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarDayViewProps) {
  // Generate time slots (8:00-17:00, 15-minute intervals)
  const timeSlots: string[] = [];
  for (let hour = 8; hour < 17; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      timeSlots.push(`${h}:${m}`);
    }
  }

  // Get appointments for a specific time slot
  const getAppointmentsForSlot = (time: string) => {
    return appointments.filter((apt) => {
      const aptDate = parseISODateTime(apt.appointment_datetime);
      const aptTime = getTimeFromISO(apt.appointment_datetime);
      const aptTimeMinutes = timeToMinutes(aptTime);
      const slotTimeMinutes = timeToMinutes(time);

      // Check if appointment starts at or overlaps this slot
      return (
        isSameDay(aptDate, date) &&
        aptTimeMinutes <= slotTimeMinutes &&
        aptTimeMinutes + apt.duration_minutes > slotTimeMinutes
      );
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-xl font-semibold text-gray-900">{formatDate(date)}</h2>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-[80px_1fr]">
          {/* Time column */}
          <div className="border-r border-gray-200">
            {timeSlots.map((time) => {
              const isHourMark = time.endsWith(':00');
              return (
                <div
                  key={time}
                  className={`px-3 py-2 text-right text-sm ${
                    isHourMark ? 'font-medium text-gray-700' : 'text-gray-400'
                  }`}
                  style={{ height: '80px' }}
                >
                  {isHourMark ? time : ''}
                </div>
              );
            })}
          </div>

          {/* Appointments column */}
          <div className="relative">
            {timeSlots.map((time) => {
              const slotAppointments = getAppointmentsForSlot(time);
              const isHourMark = time.endsWith(':00');

              return (
                <div
                  key={time}
                  className={`border-t ${isHourMark ? 'border-gray-300' : 'border-gray-100'} hover:bg-blue-50 cursor-pointer transition-colors relative`}
                  style={{ height: '80px' }}
                  onClick={() => {
                    if (slotAppointments.length === 0) {
                      onTimeSlotClick(time);
                    }
                  }}
                >
                  {slotAppointments.map((apt) => {
                    const aptTime = getTimeFromISO(apt.appointment_datetime);
                    const isFirstSlot = aptTime === time;

                    if (!isFirstSlot) return null;

                    const statusColors = getStatusColor(apt.status);
                    const durationSlots = Math.ceil(apt.duration_minutes / 15);
                    const height = durationSlots * 80;

                    return (
                      <div
                        key={apt.id}
                        className={`absolute inset-x-2 border-l-4 ${statusColors.border} ${statusColors.bg} rounded-lg p-3 overflow-hidden cursor-pointer ${statusColors.hover} shadow-sm z-10`}
                        style={{
                          height: `${height - 8}px`,
                          top: '4px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-semibold truncate">
                              {apt.patient_name}
                            </div>
                            <div className="text-sm text-gray-600 truncate">
                              {apt.appointment_type}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2 text-xs text-gray-500">
                            {formatDuration(apt.duration_minutes)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>{formatTime(apt.appointment_datetime)}</span>
                          </div>
                        </div>
                        {apt.provider_name && (
                          <div className="text-sm text-gray-600 mt-1 truncate">
                            {apt.provider_name}
                          </div>
                        )}
                        {apt.reason && (
                          <div className="text-sm text-gray-600 mt-2 line-clamp-2">
                            {apt.reason}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
